import { useCallback, useRef } from "react";
import { useApplicationContext } from "../context/ApplicationContext";
import { createLogger } from "../utils/logging";
import { isAuthPassEnabled } from "../security/authHelpers";
import {
  validateSession,
  getUserProfile,
  getAbilitiesForTenant,
  getTenantIds,
  getSessionId,
  buildUserAliasRequest,
  isNoAuthMode,
  createDGRoles,
} from "../api";
import {
  getScreenConfig,
  getUserPreferences,
  createUserAlias,
} from "../api/user.api";

const logger = createLogger("useAuthentication");

interface UseAuthenticationProps {
  onUpdateTenantDispatch: (tenantId: string) => void;
  onInvalidSession?: () => void;
  /**
   * Called synchronously just before setCurrentTenant fires during initialization.
   * Application.tsx uses this to pre-populate lastFetchedTenantRef so the second
   * useEffect's updateTenant guard blocks the otherwise-duplicate ability fetch.
   */
  onAbilitiesReady?: (tenantId: string) => void;
}

/**
 * useAuthentication - Custom hook for authentication and session management
 *
 * Handles:
 * - Session validation
 * - User profile fetching
 * - User preferences
 * - Configuration loading
 * - Initial tenant/abilities setup
 *
 * Migrated to use new API layer (Axios-based)
 */
export const useAuthentication = ({
  onUpdateTenantDispatch,
  onInvalidSession,
  onAbilitiesReady,
}: UseAuthenticationProps) => {
  const { setUser, setAbilities, setAbilitiesLoaded, setTenantIds, setCurrentTenant, tenantIds, currentTenant } =
    useApplicationContext();

  // Keep tenantIds in a ref so getUserPref can read the latest value
  // without needing to be in the dependency array (prevents re-creation chain)
  const tenantIdsRef = useRef(tenantIds);
  tenantIdsRef.current = tenantIds;

  // Keep currentTenant in a ref so validateSessionId always reads the latest
  // value without needing it in the dependency array
  const currentTenantRef = useRef(currentTenant);
  currentTenantRef.current = currentTenant;

  // Guard to ensure user profile is only fetched once per mount
  const userProfileFetchedRef = useRef(false);

  /**
   * Validate session ID and show modal if invalid
   */
  const validateSessionId = useCallback(async () => {
    const tenantId = currentTenantRef.current;
    if (!tenantId) {
      return;
    }
    try {
      const isValid = await validateSession(tenantId);
      if (isValid === false && onInvalidSession) {
        onInvalidSession();
      }
    } catch (error) {
      // Treat thrown errors (401, 500, network) as invalid session
      logger.error("Session Validation Failed", "Session invalid or expired", {
        error,
      });
      if (onInvalidSession) {
        onInvalidSession();
      }
    }
  }, [onInvalidSession]);

  /**
   * Get user preferences and set default tenant
   */
  const getUserPref = useCallback(
    async (screenId: string, userAliasName: string) => {
      try {
        const data = await getUserPreferences(screenId, userAliasName);

        // Store default tenant in sessionStorage if it exists
        // Read from ref to avoid stale closure without adding tenantIds as dep
        const defaultTenant = data?.["default"];
        if (defaultTenant && tenantIdsRef.current.includes(defaultTenant)) {
          onUpdateTenantDispatch(defaultTenant);
          sessionStorage.setItem("tenantId", defaultTenant);
        }
      } catch (error) {
        logger.error(
          "User Preferences Failed",
          "Unable to load user preferences",
          { error, screenId, userAliasName },
        );
      }
    },
    // tenantIds removed from deps — read via ref to stabilize the callback
    [onUpdateTenantDispatch],
  );

  /**
   * Get user profile and set up user alias
   */
  const getUserProfileData = useCallback(async () => {
    // In no-auth mode, load from local mock. In authenticated mode, require a valid session.
    const noAuth = isNoAuthMode();
    const sessionId = getSessionId();

    if (!noAuth && !sessionId) {
      return;
    }

    // Prevent duplicate fetches caused by initializeAuth re-running due to context updates
    if (userProfileFetchedRef.current) {
      return;
    }
    userProfileFetchedRef.current = true;

    try {
      const data = await getUserProfile();

      if (isAuthPassEnabled()) {
        if (!data?.given_name) {
          return;
        }

        const displayName = `${data.given_name} ${data.family_name}`;
        setUser(displayName, data.preferred_username ?? '');
      } else {
        if (!data?.decodedIdToken?.given_name) {
          return;
        }

        const displayName = `${data.decodedIdToken.given_name} ${data.decodedIdToken.family_name}`;
        setUser(displayName, data.decodedIdToken.preferred_username ?? '');
      }

      // In no-auth/local mode, createUserAlias is skipped — no need to fetch tenant/ability data here.
      if (isNoAuthMode()) {
        return;
      }

      const tenantData = await getTenantIds();

      if (tenantData && tenantData.length > 0) {
        const abilityData = await getAbilitiesForTenant(tenantData[0]);

        if (abilityData?.roles) {
          const request = buildUserAliasRequest(data, abilityData.roles);
          const userAliasData = await createUserAlias(request);

          if (userAliasData && "userAliasName" in userAliasData) {
            const aliasName = userAliasData.userAliasName;
            await getUserPref("2", aliasName);
          }
        }
      }
    } catch (error) {
      logger.error("User Profile Failed", "Unable to load user profile", {
        error,
      });
    }
  }, [setUser, getUserPref]);

  /**
   * Get configuration profile
   */
  const getConfProfile = useCallback(async (screenId: string) => {
    try {
      const data = await getScreenConfig(screenId);

      // Return early if no data
      if (!data || !data[0]?.screenConfiguration) {
        return;
      }

      const parsable = data[0].screenConfiguration;
      const parsed = JSON.parse(parsable);
      sessionStorage.setItem("confProfile", JSON.stringify(parsed));
    } catch (error) {
      logger.warn(
        "Config Profile Failed",
        "Unable to load configuration profile",
        { error, screenId },
      );
    }
  }, []);

  /**
   * Initialize authentication - loads tenants and abilities
   */
  const initializeAuth = useCallback(async (): Promise<{
    tenant: string;
    abilities: string[];
  }> => {
    const env = (window as any)["environments"];

    // Always fetch user profile data (both local and deployed)
    await getUserProfileData();
    
    if (env !== "local") {
      await getConfProfile("2");
    }

    try {
      // Get initial tenant list
      const fetchedRoles = await getTenantIds();

      // Snapshot tenantIds BEFORE any dispatches.
      // tenantIdsRef.current is updated on every render; calling setTenantIds below can
      // trigger a synchronous re-render (React 17 legacy mode flushes outside event handlers)
      // which would update the ref before we finish this function. Capturing here preserves
      // the "was this tenant already known?" semantics of the original closure-based check.
      const prevTenantIds = tenantIdsRef.current;

      // Avoid dispatching if roles haven't changed (prevents unnecessary re-renders)
      // Use ref to avoid adding tenantIds to the useCallback deps (which caused initializeAuth
      // to be recreated on every setTenantIds call, re-triggering the useEffect in Application).
      const sameRoles =
        fetchedRoles.length === (prevTenantIds?.length || 0) &&
        fetchedRoles.every((r: string, i: number) => prevTenantIds?.[i] === r);

      if (!sameRoles) {
        setTenantIds(fetchedRoles);
      }

      if (fetchedRoles && fetchedRoles.length > 0) {
        const tenant = fetchedRoles[0];

        // Set tenantId and currentTenant BEFORE fetching abilities.
        // useNavigationFilter requires both `tenantId` AND `abilities` to be non-empty
        // before it renders the ability-filtered menu. Setting tenant first ensures the
        // navigation filter's useMemo has a valid tenantId when setAbilities fires.
        if (!prevTenantIds || prevTenantIds[0] !== tenant) {
          onUpdateTenantDispatch(tenant);
          // Signal Application.tsx to pre-populate lastFetchedTenantRef BEFORE setCurrentTenant.
          // This means when setCurrentTenant triggers the second useEffect, updateTenant's own
          // guard (lastFetchedTenantRef === tenantId) will block the duplicate ability fetch.
          // On error below, onAbilitiesReady is NOT called, leaving the ref unset so the
          // second useEffect can still call updateTenant as an error-recovery retry.
          onAbilitiesReady?.(tenant);
          setCurrentTenant(tenant);
          sessionStorage.setItem("tenantFirstFound", tenant);
        }

        // Fire-and-forget: create/update the DAC roles mapping for the fetched
        // tenants. createDGRoles internally decides (via isAuthPassEnabled) whether
        // to include the tenant in the request, and never rejects, so no .catch
        // is needed here.
        createDGRoles(fetchedRoles, tenant);

        // Fetch abilities after tenant is set. On success, call setAbilitiesLoaded(true)
        // BEFORE returning so the Application.tsx useEffect guard sees it as already loaded
        // (preventing a duplicate fetch). On failure, leave abilitiesLoaded=false so the
        // second useEffect in Application.tsx can still retry via updateTenant.
        let abilitiesList: string[] = [];
        try {
          const abilityData = await getAbilitiesForTenant(tenant);
          const result = abilityData?.abilities;
          abilitiesList = result?.["all-abilities"] ?? [];
          if (abilitiesList.length > 0) {
            setAbilities(abilitiesList);
          }
          setAbilitiesLoaded(true);
        } catch (abilityError) {
          logger.warn(
            "Abilities Fetch Failed",
            "Failed to load abilities for initial tenant; second useEffect will retry",
            { abilityError, tenant },
          );
          // abilitiesLoaded intentionally NOT set — second useEffect retries via updateTenant
        }

        return { tenant, abilities: abilitiesList };
      }

      return { tenant: "", abilities: [] };
    } catch (error) {
      logger.error(
        "Auth Initialization Failed",
        "Unable to initialize authentication",
        { error },
      );
      return { tenant: "", abilities: [] };
    }
  }, [
    getUserProfileData,
    getConfProfile,
    setTenantIds,
    setCurrentTenant,
    onUpdateTenantDispatch,
    setAbilities,
    setAbilitiesLoaded,
    // tenantIds intentionally omitted — read via tenantIdsRef.current to avoid
    // recreating this callback on every setTenantIds dispatch, which previously
    // caused initializeAuth to re-run on every render cycle.
  ]);

  return {
    validateSessionId,
    getUserProfile: getUserProfileData,
    getUserPref,
    getConfProfile,
    initializeAuth,
  };
};
